// src/app/components/admin/global-settings-page.tsx
//
// Upmode Global Settings — a full page whose SECTIONS are chosen from a left
// navigation pane, mirroring the user Settings page layout (280px aside on
// desktop, off-canvas drawer on mobile, section content in the main column).
//
// It consolidates the platform-wide admin areas that used to be separate items
// in the top-nav menu (User Management, Associate Brands, Brand API Keys) and
// adds a Platform section for global switches like the Performance data source.
// Admin-only: the route is gated in App.tsx. Each embedded section is the same
// self-contained component the standalone admin pages render — no duplicated
// logic; this page is just the shell + section navigation.

import { useEffect, useState, type ComponentType, type ReactNode, type CSSProperties } from "react";
import {
  Settings as SettingsIcon,
  SlidersHorizontal,
  Users,
  Tag,
  KeyRound,
  ChevronRight,
  PanelLeft,
  Rocket,
  X,
  Activity,
  ScrollText,
} from "lucide-react";

import { useResponsive } from "@/app/hooks/common/use-breakpoint";
import { AdminUsersPage } from "@/app/components/login/admin/admin-users-page";
import { AdminBrandAccessPage } from "@/app/components/login/admin/admin-brand-access-page";
import { AdminBrandKeysPage } from "@/app/components/login/admin/admin-brand-keys-page";
import { PlatformSettingsSection } from "./platform-settings-section";
import { HealthDetailsSection } from "./health-details-section";
import { LogsSection } from "./logs-section";

export type GlobalSettingsSection = "platform" | "users" | "brands" | "keys" | "health" | "logs";

interface SectionDef {
  id: GlobalSettingsSection;
  label: string;
  icon: ComponentType<{ style?: CSSProperties }>;
  color: string;
  render: () => ReactNode;
}

const SECTIONS: SectionDef[] = [
  {
    id: "platform",
    label: "Platform",
    icon: SlidersHorizontal,
    color: "var(--alpha-teal)",
    // Its own index-and-detail shell: the platform switches outgrew a single
    // stacked page. See platform-settings-section.tsx.
    render: () => <PlatformSettingsSection />,
  },
  { id: "users",  label: "User Management",  icon: Users,    color: "var(--spectral-violet)", render: () => <AdminUsersPage /> },
  { id: "brands", label: "Associate Brands", icon: Tag,      color: "var(--signal-blue)",     render: () => <AdminBrandAccessPage /> },
  { id: "keys",   label: "Brand API Keys",   icon: KeyRound, color: "var(--chart-4)",         render: () => <AdminBrandKeysPage /> },
  // Read-only operational views (UPM-169): what build is running, whether its
  // dependencies answer, which migrations are applied, and the captured logs.
  { id: "health", label: "Health Details",   icon: Activity,   color: "var(--signal-green)",  render: () => <HealthDetailsSection /> },
  { id: "logs",   label: "Logs",             icon: ScrollText, color: "var(--signal-blue)",   render: () => <LogsSection /> },
];

interface GlobalSettingsPageProps {
  onNavigate?: (page: string) => void;
  initialSection?: GlobalSettingsSection;
}

export function GlobalSettingsPage({ onNavigate, initialSection = "platform" }: GlobalSettingsPageProps) {
  const [active, setActive] = useState<GlobalSettingsSection>(initialSection);
  const { isDesktop, isMobile } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeSection = SECTIONS.find((s) => s.id === active) ?? SECTIONS[0];

  // Close the drawer when we grow to desktop.
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  // Lock body scroll while the mobile drawer is open.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  const mainPad = isMobile ? 20 : isDesktop ? 40 : 28;

  // ── Left nav content (aside on desktop, drawer on mobile) ─────────────────
  const navContent = (
    <>
      <div
        style={{
          height: "89px",
          borderBottom: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "0 24px",
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "var(--alpha-teal-10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SettingsIcon style={{ width: "16px", height: "16px", color: "var(--alpha-teal)" }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)", lineHeight: 1.2 }}>
              Global Settings
            </h2>
            <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>Admin · platform-wide</p>
          </div>
          {!isDesktop && (
            <button
              onClick={() => setDrawerOpen(false)}
              aria-label="Close settings menu"
              style={{
                marginLeft: "auto",
                padding: 6,
                borderRadius: 7,
                border: "1px solid var(--border)",
                backgroundColor: "var(--muted)",
                cursor: "pointer",
                color: "var(--muted-foreground)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <X style={{ width: 14, height: 14 }} />
            </button>
          )}
        </div>
      </div>

      <nav style={{ flex: 1, paddingTop: "16px", overflowY: "auto" }}>
        {SECTIONS.map((section) => {
          const Icon = section.icon;
          const isActive = active === section.id;
          return (
            <button
              key={section.id}
              onClick={() => {
                setActive(section.id);
                setDrawerOpen(false);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px 24px",
                backgroundColor: isActive ? "var(--muted)" : "transparent",
                border: "none",
                borderLeft: isActive ? `3px solid ${section.color}` : "3px solid transparent",
                cursor: "pointer",
                transition: "all 0.2s ease",
                textAlign: "left",
              }}
            >
              <Icon style={{ width: "18px", height: "18px", color: isActive ? section.color : "var(--muted-foreground)", flexShrink: 0 }} />
              <span
                style={{
                  fontSize: "var(--text-sm)",
                  fontWeight: isActive ? "var(--font-weight-semibold)" : "var(--font-weight-medium)",
                  color: isActive ? "var(--foreground)" : "var(--muted-foreground)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {section.label}
              </span>
              {isActive && <ChevronRight style={{ width: "16px", height: "16px", color: section.color, flexShrink: 0 }} />}
            </button>
          );
        })}
      </nav>

      {/* Onboard is a flow, not a section — keep it as a quick action in the footer. */}
      <div style={{ padding: "16px 24px 24px", flexShrink: 0, borderTop: "1px solid var(--border)" }}>
        <button
          type="button"
          onClick={() => onNavigate?.("onboarding")}
          className="w-full inline-flex items-center justify-center gap-2"
          style={{
            padding: "10px 14px",
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
            backgroundColor: "transparent",
            cursor: "pointer",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-weight-medium)",
            color: "var(--foreground)",
          }}
        >
          <Rocket style={{ width: 16, height: 16 }} />
          Onboard a customer
        </button>
      </div>
    </>
  );

  return (
    // No min-h-screen here. This page renders BELOW the shared SiteHeader inside
    // a shell that is already min-h-screen, so claiming another 100vh made the
    // document permanently taller than the viewport by the height of that nav —
    // a dead band you could always scroll into, with nothing in it.
    <div className="flex" style={{ backgroundColor: "var(--background)" }}>
      {/* Left nav — inline aside on desktop, off-canvas drawer otherwise */}
      {isDesktop ? (
        <aside
          style={{
            width: "280px",
            borderRight: "1px solid var(--border)",
            backgroundColor: "var(--background)",
            flexShrink: 0,
          }}
        >
          {/* Sticky inner column rather than a 100vh aside. Stretching to the
              row keeps the divider running the full length of the content
              (it used to stop dead at 100vh), while the nav itself stays put
              as the section scrolls. */}
          <div
            style={{
              position: "sticky",
              top: 0,
              maxHeight: "100vh",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {navContent}
          </div>
        </aside>
      ) : (
        <>
          {drawerOpen && (
            <div
              onClick={() => setDrawerOpen(false)}
              style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 45 }}
              aria-hidden="true"
            />
          )}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              height: "100dvh",
              width: "min(320px, 85vw)",
              zIndex: 46,
              backgroundColor: "var(--background)",
              borderRight: "1px solid var(--border)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
              transition: "transform 300ms ease-in-out",
              boxShadow: drawerOpen ? "0 0 30px rgba(0,0,0,0.15)" : "none",
            }}
            aria-hidden={!drawerOpen}
          >
            {navContent}
          </div>
        </>
      )}

      {/* Section content */}
      {/* The section content scrolls with the document. It used to be its own
          overflow:auto box at minHeight 100vh, which put a second scrollbar
          inside the first and left a tall empty tail under short sections. */}
      <main
        className="flex-1"
        style={{
          backgroundColor: "var(--background)",
          minWidth: 0,
          paddingTop: isDesktop ? 0 : `${mainPad}px`,
          paddingBottom: 24,
        }}
      >
        {/* Mobile strip — open-drawer toggle + current section */}
        {!isDesktop && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              margin: `0 ${mainPad}px 20px`,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid var(--border)",
              backgroundColor: "var(--muted-30)",
            }}
          >
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open settings sections"
              title="Settings sections"
              style={{
                padding: 8,
                borderRadius: 8,
                border: "1px solid var(--border)",
                backgroundColor: "var(--background)",
                cursor: "pointer",
                color: "var(--foreground)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PanelLeft style={{ width: 16, height: 16 }} />
            </button>
            <activeSection.icon style={{ width: 16, height: 16, color: activeSection.color, flexShrink: 0 }} />
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: "var(--text-sm)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--foreground)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {activeSection.label}
            </div>
          </div>
        )}

        {activeSection.render()}
      </main>
    </div>
  );
}

export default GlobalSettingsPage;
